import React, { useState, useEffect } from 'react';
import { X, ChevronRight, ChevronLeft } from 'lucide-react';
import { BakingSession, FERMENTATION_PROCESSES, DEFAULT_BAKING_DURATION, Starter } from '../../types/baking';
import { FermentationOption } from './FermentationOption';
import { FermentationTimeSlider } from './FermentationTimeSlider';
import { OvenSelector } from './OvenSelector';
import { StarterSelector } from './StarterSelector';
import { StarterScheduleSection } from './StarterScheduleSection';

interface NewBakeModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (sessionData: Partial<BakingSession>) => void;
  starters: Starter[];
  existingSessions?: BakingSession[];
}

export function NewBakeModal({ 
  isOpen, 
  onClose, 
  onSubmit, 
  starters,
  existingSessions = []
}: NewBakeModalProps) {
  const [step, setStep] = useState(1);
  const [timeError, setTimeError] = useState<string | null>(null);
  const [isTimeValid, setIsTimeValid] = useState(false);
  const [sessionData, setSessionData] = useState<Partial<BakingSession>>({
    name: '',
    targetBakeTime: new Date(new Date().getTime() + 8 * 60 * 60 * 1000 - new Date().getTimezoneOffset() * 60000).toISOString().slice(0, 16),
    fermentationType: '',
    ovenType: 'domestic',
    woodOvenDetails: undefined,
    starterId: '',
    status: 'scheduled',
    customBulkTime: undefined,
    customProofTime: undefined,
    starterSchedule: {
      feeds: [],
      customized: false
    }
  });

  useEffect(() => {
    if (isOpen) {
      setStep(1);
      setIsTimeValid(false);
      setTimeError(null);
      setSessionData({
        name: '',
        targetBakeTime: new Date(new Date().getTime() + 8 * 60 * 60 * 1000 - new Date().getTimezoneOffset() * 60000).toISOString().slice(0, 16),
        fermentationType: '',
        ovenType: 'domestic',
        woodOvenDetails: undefined,
        starterId: '',
        status: 'scheduled',
        customBulkTime: undefined,
        customProofTime: undefined,
        starterSchedule: {
          feeds: [],
          customized: false
        }
      });
    }
  }, [isOpen]);

  // Check if a process is available based on target bake time
  const isProcessAvailable = (processId: string) => {
    if (!sessionData.targetBakeTime) return FERMENTATION_PROCESSES;
    
    const timeDiff = new Date(sessionData.targetBakeTime).getTime() - new Date().getTime();
    const hoursUntilBake = timeDiff / (1000 * 60 * 60);
    
    // If less than 18 hours until bake time, only direct fermentation is available
    if (hoursUntilBake < 18) {
      return processId === 'direct';
    }
    
    return true;
  };

  const handleNext = () => {
    if (step < 5) {
      setStep(step + 1);
    } else {
      const formattedData = {
        ...sessionData,
        targetBakeTime: sessionData.targetBakeTime ? new Date(sessionData.targetBakeTime) : new Date(),
        starterSchedule: {
          ...sessionData.starterSchedule,
          feeds: sessionData.starterSchedule?.feeds.map(feed => ({
            ...feed,
            scheduledTime: new Date(feed.scheduledTime)
          })) || []
        }
      };
      onSubmit(formattedData);
      onClose();
    }
  };

  const handleBack = () => {
    if (step > 1) setStep(step - 1);
  };

  // Validate target bake time whenever it changes
  useEffect(() => {
    if (!sessionData.targetBakeTime) {
      setIsTimeValid(true);
      setTimeError(null);
      return;
    }

    const timeDiff = new Date(sessionData.targetBakeTime).getTime() - new Date().getTime();
    const hoursUntilBake = timeDiff / (1000 * 60 * 60);
    
    if (hoursUntilBake < 6) {
      setIsTimeValid(false);
      setTimeError('Baking great bread takes time! Your target bake time is too close. Please allow a minimum of 6 hours for your fermentation time.');
    } else {
      setIsTimeValid(true);
      setTimeError(null);
    }
  }, [sessionData.targetBakeTime]);

  const isStepValid = () => {
    switch (step) {
      case 1:
        return sessionData.name.trim() !== '' && isTimeValid;
      case 2:
        return sessionData.fermentationType;
      case 3:
        return sessionData.ovenType;
      case 4:
        return sessionData.starterId;
      case 5:
        return sessionData.starterSchedule?.feeds.length > 0;
      default:
        return false;
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[200] p-4">
      <div className="bg-gradient-to-br from-[#1C1C1C] to-[#888888] shadow-lg w-full max-w-lg max-h-[90vh] overflow-y-auto text-white">
        <div className="p-6 border-b border-white/10">
          <div className="flex justify-between items-center">
            <h2 className="text-2xl text-white font-brand font-light italic">New Baking Session</h2>
            <button 
              onClick={onClose}
              className="text-white/70 hover:text-white transition-colors p-1 hover:bg-white/10 rounded-full"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
          <div className="mt-4 flex gap-2">
            {[1, 2, 3, 4, 5].map((stepNumber) => (
              <div
                key={stepNumber}
                className={`flex-1 h-2 rounded-full transition-all duration-300 ${
                  step >= stepNumber ? 'bg-amber-400' : 'bg-white/10'
                }`}
              />
            ))}
          </div>
        </div>

        <div className="p-6">
          {step === 1 && (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-white/90 mb-1">
                  Session Name
                </label>
                <input
                  type="text"
                  value={sessionData.name}
                  onChange={(e) => setSessionData({ ...sessionData, name: e.target.value })}
                  className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder:text-white/50 focus:border-amber-300/50 focus:ring-2 focus:ring-amber-300/20"
                  placeholder="e.g., Weekend Sourdough"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-white/90 mb-1">
                  Target Bake Time
                </label>
                <input
                  type="datetime-local"
                  value={sessionData.targetBakeTime}
                  onChange={(e) => {
                    setSessionData({ ...sessionData, targetBakeTime: e.target.value });
                  }}
                  className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white focus:border-amber-300/50 focus:ring-2 focus:ring-amber-300/20"
                />
                {timeError && sessionData.targetBakeTime && (
                  <div className="mt-2 p-3 bg-red-500/60 backdrop-blur-sm rounded-lg border border-red-500/20">
                    <p className="text-sm text-red-200">{timeError}</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-4">
              <h3 className="font-medium mb-4">Select Fermentation Process</h3>
              <div className="space-y-4">
                <FermentationOption
                  key="direct"
                  process={FERMENTATION_PROCESSES[0]}
                  selected={sessionData.fermentationType === 'direct'}
                  onSelect={() => {
                    const available = isProcessAvailable('direct');
                    if (available) {
                      setSessionData({ 
                        ...sessionData, 
                        fermentationType: 'direct',
                        customBulkTime: undefined,
                        customProofTime: undefined
                      });
                    }
                  }}
                  disabled={!isProcessAvailable('direct')}
                  customTime={undefined}
                  targetBakeTime={sessionData.targetBakeTime}
                />

                {/* Cold Bulk Fermentation */}
                <FermentationOption
                  key="cold-bulk"
                  process={FERMENTATION_PROCESSES[1]}
                  selected={sessionData.fermentationType === 'cold-bulk'}
                  onSelect={() => {
                    const available = isProcessAvailable('cold-bulk');
                    if (available) {
                      setSessionData({ 
                        ...sessionData, 
                        fermentationType: 'cold-bulk',
                        customBulkTime: sessionData.customBulkTime || 24,
                        customProofTime: undefined
                      });
                    }
                  }}
                  disabled={!isProcessAvailable('cold-bulk')}
                  customTime={sessionData.fermentationType === 'cold-bulk' ? sessionData.customBulkTime : undefined}
                  onCustomTimeChange={(value) => {
                    setSessionData(prev => ({
                      ...prev,
                      customBulkTime: value
                    }));
                  }}
                  targetBakeTime={sessionData.targetBakeTime}
                />

                {/* Room Temperature Fermentation */}
                <FermentationOption
                  key="slow-room"
                  process={FERMENTATION_PROCESSES[2]}
                  selected={sessionData.fermentationType === 'slow-room'}
                  onSelect={() => {
                    const available = isProcessAvailable('slow-room');
                    if (available) {
                      setSessionData({ 
                        ...sessionData, 
                        fermentationType: 'slow-room',
                        customBulkTime: undefined,
                        customProofTime: undefined
                      });
                    }
                  }}
                  disabled={!isProcessAvailable('slow-room')}
                  customTime={undefined}
                  targetBakeTime={sessionData.targetBakeTime}
                />

                {/* Cold Retard */}
                <FermentationOption
                  key="pre-proofed"
                  process={FERMENTATION_PROCESSES[3]}
                  selected={sessionData.fermentationType === 'pre-proofed'}
                  onSelect={() => {
                    const available = isProcessAvailable('pre-proofed');
                    if (available) {
                      setSessionData({ 
                        ...sessionData, 
                        fermentationType: 'pre-proofed',
                        customBulkTime: undefined,
                        customProofTime: sessionData.customProofTime || 24
                      });
                    }
                  }}
                  disabled={!isProcessAvailable('pre-proofed')}
                  customTime={sessionData.fermentationType === 'pre-proofed' ? sessionData.customProofTime : undefined}
                  onCustomTimeChange={(value) => {
                    setSessionData(prev => ({
                      ...prev,
                      customProofTime: value
                    }));
                  }}
                  targetBakeTime={sessionData.targetBakeTime}
                />
              </div>
            </div>
          )}

          {step === 3 && (
            <OvenSelector
              value={sessionData.ovenType}
              onChange={(type, details) => 
                setSessionData({ 
                  ...sessionData, 
                  ovenType: type, 
                  woodOvenDetails: details 
                })
              }
            />
          )}

          {step === 4 && (
            <div className="space-y-4">
              <h3 className="font-medium mb-4">Select Starter</h3>
              <StarterSelector
                starters={starters}
                selectedStarterId={sessionData.starterId}
                onSelect={(id) => setSessionData({ ...sessionData, starterId: id })}
                existingSessions={existingSessions}
                targetBakeTime={sessionData.targetBakeTime ? new Date(sessionData.targetBakeTime) : undefined}
              />
            </div>
          )}

          {step === 5 && (
            <StarterScheduleSection
              sessionData={sessionData}
              setSessionData={setSessionData}
              existingSessions={existingSessions}
            />
          )}
        </div>

        <div className="p-6 border-t border-white/10 bg-white/5">
          <div className="flex justify-between">
            {step > 1 ? (
              <button
                onClick={handleBack}
                className="flex items-center gap-2 px-6 py-2 border border-white/20 rounded-xl hover:bg-white/10 transition-colors"
              >
                <ChevronLeft className="w-4 h-4" />
                Back
              </button>
            ) : (
              <div></div>
            )}
            <button
              onClick={handleNext}
              disabled={!isStepValid()}
              className={`flex items-center gap-2 px-6 py-2 rounded-xl transition-all duration-300 ${
                isStepValid()
                  ? 'bg-gradient-to-r from-amber-500/90 to-amber-600/90 hover:from-amber-500 hover:to-amber-600'
                  : 'bg-white/10 text-white/50 cursor-not-allowed'
              }`}
            >
              {step === 5 ? 'Create Session' : 'Next'}
              {step !== 5 && <ChevronRight className="w-4 h-4" />}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}